//Sketch.tsx ==============================================================
import "../Util/Prototypes";

import * as Default from "../Default";
import * as DrawerMenu from "../Menus/DrawerMenu";
import * as GraLay from "../Util/GraLay";
import * as Highlight from "../Util/Highlight";
import * as MainMap from "../MainMap";
import * as Settings from "../Settings";
import * as Util from "../Util/Util";
import * as Util_App from "../Util/Util_App";
import * as WidgetHeading from "./WidgetHeading";

import { Button, ButtonGroup, Checkbox, FormControlLabel, Icon, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Typography } from "@mui/material";

import { yellow } from '@mui/material/colors';

import React from "react";

//ESRI javascript API.
import CIMSymbol from "@arcgis/core/symbols/CIMSymbol";
import Color from "@arcgis/core/Color";
import FeatureSnappingLayerSource from "@arcgis/core/views/interactive/snapping/FeatureSnappingLayerSource";
import Graphic from "@arcgis/core/Graphic";
import Point from "@arcgis/core/geometry/Point";
import Polygon from "@arcgis/core/geometry/Polygon";
import Polyline from "@arcgis/core/geometry/Polyline";
import * as projection from "@arcgis/core/geometry/projection";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import SimpleLineSymbol from "@arcgis/core/symbols/SimpleLineSymbol";
import SimpleMarkerSymbol from "@arcgis/core/symbols/SimpleMarkerSymbol";
import SpatialReference from "@arcgis/core/geometry/SpatialReference";
import TextSymbol from "@arcgis/core/symbols/TextSymbol";

const toolName = "Sketch";
const toolDiv = "Sketch_div";

type markerStyleType = "circle" | "square" | "cross" | "x" | "diamond" | "triangle" | "path";
type lineStyleType = "dash" | "dash-dot" | "dot" | "long-dash" | "long-dash-dot" | "long-dash-dot-dot" | "none" | "short-dash" | "short-dash-dot" | "short-dash-dot-dot" | "short-dot" | "solid";

type createToolType = "point" | "polyline" | "polygon" | "rectangle" | "circle";
type activeToolType = "point" | "polyline" | "polygon" | "rectangle" | "circle" | "move" | "transform" | "reshape" | "rectangle-selection" | "lasso-selection";

const createTools = ["point", "polyline", "polygon", "rectangle", "circle"];
const polygonTools = ["polygon", "rectangle", "circle"];

let prevDrawingTool:  "point" | "polyline" | "polygon" | "rectangle" | "circle" = null;
let drawingToolIsText = false;

let createState: "start" | "active" | "complete" | "cancel" = null;
let updateState: "start" | "active" | "complete" = null;

let sketchW: __esri.Sketch;    // Sketch widget.
let sketchVM: __esri.SketchViewModel; // Sketch view model.
let textGra: __esri.Graphic;
let addLabelCoord = false;
let labelFormatText = "Lat/Long DM";

let click_eventHandler: IHandle;
let pointerMove_eventHandler: IHandle;

//let changingCreateMode: boolean = false; //Uncomment all changingCreateMode code for hybrid create option.
let helpMsg: string;

//Symbology.
interface iPointSym {
   markerType: string; //"Simple" or "Picture".
   color: string;
   style: string; // Point symbol name or Picture name.
   size: string;
   visible: boolean;
   simpleMarkerSym: __esri.SimpleMarkerSymbol;
   pictureMarkerSym: __esri.CIMSymbol;
}
interface iLineSym {
   color: string;
   style: string;
   width: string;
   visible: boolean;
}
interface iPolySym {
   color: string;
   opacity: string;
   width: string; //Outline width;
   visible?: boolean;
}
interface iTextSym {
   color: string;
   font: string;
   size: string;
   text: string;
   visible: boolean;
}

const pointSym: iPointSym = {
   markerType: "Simple",
   color: "Red",
   style: "circle",
   size: "14",
   visible: false,
   simpleMarkerSym: null,
   pictureMarkerSym: null,
};

const lineSym: iLineSym = {
   color: "Red",
   style: "solid",
   width: "2",
   visible: false,
};

const polySym: iPolySym = {
   color: "Red",
   opacity: "0.2",
   width: "1.5",
   visible: false,
};

const textSym: iTextSym = {
   color: "Black",
   font: "Sans-serif",
   size: "14",
   text: "",
   visible: false,
};

const textSymbol = new TextSymbol({
   angle: 0,
   color: textSym.color.toLowerCase(),
   font: {
      family: textSym.font.toLowerCase(),
      size: parseInt(textSym.size, 10),
      weight: "normal",
   },
   haloColor: "white",
   haloSize: 1,
   text: "",
});

//Clear this tool.
export function clearTool() {
}

interface iProps {
}

//Render function.
export function Render(props: iProps) {
   //Util.logMsg("Top of SelectOnMap Render");

   //Close this tool.
   function close() {
      DrawerMenu.closeTool();
   }

   //Set refresh method.
   const [ignored, refresh] = React.useReducer((x: number) => x + 1, 0);

   //Component did mount.
   React.useEffect(() => {
      //Load the sketch widget module.
      import("@arcgis/core/widgets/Sketch").then(({ default: SketchWidget }) => {

         //Load snapping feature layers.
         const sketchSource = new FeatureSnappingLayerSource({ layer: GraLay.sketch });
         const measureSource = new FeatureSnappingLayerSource({ layer: GraLay.measure });
         let featureSources = [sketchSource, measureSource]; //Initialize.

         for (const layerName of Settings.sketchSnappingLayers) {
            const snappingLayer = Util_App.getOperationalLayerById(layerName) as __esri.FeatureLayer;
            if (snappingLayer) {
               const featureLayerSource = new FeatureSnappingLayerSource({ layer: snappingLayer });
               featureSources.Add(featureLayerSource);
            }
         }

         //Create the sketch widget.
         sketchW = new SketchWidget({
            container: toolDiv,
            layer: GraLay.sketch,
            view: MainMap.activeView,
            visibleElements: {
               selectionTools: {
                  "lasso-selection": true,
                  "rectangle-selection": false,
               },
               settingsMenu: false,
            },
            defaultCreateOptions: {
               mode: "click" // "hybrid" or "click" - Click keeps pan map alive.  Hybrid does not.
            },
            snappingOptions: {
               enabled: true,
               featureSources: featureSources,
            },
            tooltipOptions: { enabled: true },
         });

         //Set sketch view model values.
         sketchVM = sketchW.viewModel;

         //Drawing tool initial colors.
         const xColor = Color.fromString(pointSym.color.toLowerCase());
         const lColor = Color.fromString(lineSym.color.toLowerCase());
         const pColor = Color.fromString(polySym.color.toLowerCase());
         const oColor = Color.fromString(polySym.color.toLowerCase());
         pColor.a = Util.parseFlt(polySym.opacity);

         let markerStyle = pointSym.style;
         let pictureStyle = pointSym.style;

         if (pointSym.markerType === "Simple") {
            pictureStyle = "Arrow";

         } else {
            markerStyle = "circle";
         }

         pointSym.simpleMarkerSym = new SimpleMarkerSymbol({ color: xColor, size: pointSym.size, style: markerStyle as markerStyleType });

         pointSym.pictureMarkerSym = new CIMSymbol({
            data: {
               type: "CIMSymbolReference",
               symbol: {
                  type: "CIMPointSymbol",
                  symbolLayers: [{
                     "type": "CIMPictureMarker",
                     "enable": true,
                     size: Util.parseFlt(pointSym.size),
                     "url": "Images/" + pictureStyle + ".png",
                  }]
               }
            }
         });

         const drawLineSym = new SimpleLineSymbol({ color: lColor, width: lineSym.width, style: lineSym.style as lineStyleType });
         const drawPolygonSym = new SimpleFillSymbol({ color: pColor, style: "solid", outline: { color: oColor, width: polySym.width } });

         if (pointSym.markerType === "Simple") {
            sketchVM.pointSymbol = pointSym.simpleMarkerSym;

         } else { //Picture marker symbol.
            sketchVM.pointSymbol = pointSym.pictureMarkerSym;
         }

         sketchVM.polylineSymbol = drawLineSym;
         sketchVM.polygonSymbol = drawPolygonSym;

         // Activate previous drawing tool.
         if (prevDrawingTool) {
            Util.logMsg("Component did mount - prevDrawingTool: " + prevDrawingTool);
            setTimeout(function () { sketchW.create(prevDrawingTool as createToolType); }, 100);
         }

         //Initialize help message.
         setHelpMessage();
         refresh();

         //Drawing tool changed event.
         sketchW.watch("activeTool", function (newValue: activeToolType) {
            //Util.errMsg("activeTool changed NewValue: " + newValue + " ActiveTool: " + sketchW.activeTool);

            //For Hybrid mode - Exit function to avoid infinite loop recursion when changing creation click mode.
            //if (changingCreateMode) {
            //   setTimeout(function () { changingCreateMode = false; }, 100);
            //   return;
            //}

            ////Change to click mode when creating rectangles and circles. Hacky!
            //if (sketchW.activeTool === "rectangle") {
            //   changingCreateMode = true;
            //   sketchW.create("rectangle", { mode: "click" });
            //}
            //if (sketchW.activeTool === "circle") {
            //   changingCreateMode = true;
            //   sketchW.create("circle", { mode: "click" });
            //}

            //Disable add text mode.
            if (sketchW.activeTool) {
               drawingToolIsText = false;
            }

            //Set previous drawing tool.
            if (createTools.includes(sketchW.activeTool)) {
               prevDrawingTool = sketchW.activeTool as createToolType;
            }

            if (sketchW.activeTool === "lasso-selection") {
               prevDrawingTool = null;
            }

            //Set the sketching in progress line symbol.
            GraLay.temp.removeAll();
            resetSketchingLineSymbol();

            setHelpMessage();
            refresh();
         });

         //Sketch widget "Create" event.
         sketchW.on("create", function (event) {
            //Util.errMsg("create event.state: " + event.state + " sketchW.state: " + sketchW.state);
            createState = event.state;

            //Add coordinate label text.
            if (addLabelCoord) {
               if (event.tool === "point" && createState === "complete") {
                  setTimeout(function () { addCoordLabel(event.graphic.geometry as __esri.Point); }, 100);
               }
            }

            if (createState === "start" && event.tool !== "point") {
               setHelpMessage();
               refresh();
            }
         });

         //Sketch widget "Update" editing event.
         sketchW.on("update", function (event) {
            updateState = event.state;

            if (updateState === "start") {
               setHelpMessage();
               refresh();
            }
         });

         //Delete graphic event.
         sketchW.on("delete", function (event) {
         });

         //Map click event.
         click_eventHandler = MainMap.activeView.on("click", function (event) {
            //Add text to map.
            if (drawingToolIsText) {
               setTimeout(function () { addText(event.mapPoint); }, 100);
            }
         }); //end map click.

         //Mouse move event.
         pointerMove_eventHandler = MainMap.mapView.on("pointer-move", function (event) {
            if (drawingToolIsText && textSym.text.trim() !== "") {
               const mouseEvent = event.native as MouseEvent;

               if (!mouseEvent.shiftKey && mouseEvent.buttons === 0 && Highlight.stationary) { //Skip pan and zoom operations.
                  const screenPnt = new Point({ x: event.x, y: event.y, spatialReference: MainMap.activeView.spatialReference });
                  const pnt = MainMap.activeView.toMap({ x: screenPnt.x, y: screenPnt.y });

                  const sym = textSymbol;

                  sym.color = Color.fromString(textSym.color.toLowerCase());
                  sym.font.family = textSym.font.toLowerCase();
                  sym.font.size = Util.parseInteger(textSym.size);
                  sym.text = textSym.text;

                  sym.haloColor = Color.fromString("black");
                  if (textSym.color === "Black" || textSym.color === "Maroon" || textSym.color === "Purple" || textSym.color === "Green" || textSym.color === "Olive" || textSym.color === "Navy" || textSym.color === "Blue" || textSym.color === "Teal") {
                     sym.haloColor = Color.fromString("white");
                  }

                  textGra = new Graphic({ geometry: pnt, symbol: sym });
                  GraLay.temp.removeAll();
                  GraLay.temp.add(textGra);
               }
            }
         }); //end mouse move.

      }).catch((error: Error) => {
         console.error('An error occurred while loading the Sketch Widget module:', error);
      });

      //Component will unmount.
      return () => {
         sketchW.cancel();
         sketchW.destroy();
         sketchW = null;

         click_eventHandler.remove();
         pointerMove_eventHandler.remove();

         GraLay.temp.removeAll();
      };
   }, []); //end component did mount.

   //Set the help message.
   function setHelpMessage() {
      const activeTool = sketchW.activeTool;

      if (activeTool === "point") {
         helpMsg = 'Click on the map to draw a point';

      } else if (activeTool === "polyline") {
         helpMsg = 'Click on the map to draw a line'; // 'Click or drag on the map to draw a line'

         if (createState === "start" || createState === "active") {
            helpMsg = 'Click to continue line. Double-click to finish.'; //'Click or drag to continue. Double-click to finish.'
         }

      } else if (activeTool === "polygon") {
         helpMsg = 'Click on the map to draw a shape'; //'Click or drag on the map to draw a shape';

         if (createState === "start" || createState === "active") {
            helpMsg = 'Click to continue shape. Double-click to finish.'; //'Click or drag to continue. Double-click to finish.'
         }

      } else if (activeTool === "rectangle") {
         helpMsg = 'Click two corners on the map to draw a rectangle';

         if (createState === "start" || createState === "active") {
            helpMsg = 'Click the second corner point to finish.';
         }

      } else if (activeTool === "circle") {
         helpMsg = 'Click a center and radius point to draw a circle';

         if (createState === "start" || createState === "active") {
            helpMsg = 'Click the radius point to finish.';
         }

      } else if (drawingToolIsText) {
         helpMsg = 'Click on the map to add text';

         //Update selected graphic.
      } else if (updateState === "start" || updateState === "active") {
         helpMsg = 'Drag an anchor point or click again to edit verticies';

      } else { //No active drawing tool.
         helpMsg = 'Choose a drawing tool';
         if (GraLay.sketch.graphics.length > 0) {
            helpMsg = 'Choose a drawing tool or select a sketch to edit';
         }
      }
   }

   //Activate text drawing mode.
   function activateTextTool() {
      sketchW.cancel();
      drawingToolIsText = true;
      prevDrawingTool = null;
      setHelpMessage();
      refresh();
   }

   //Delete all graphics.
   function deleteAll() {
      GraLay.sketch.removeAll();
      setHelpMessage();
   }

   //Point symbology menus.
   const [pointColorE, setPointColorE] = React.useState<HTMLButtonElement | null>(null);
   const [pointStyleE, setPointStyleE] = React.useState<HTMLButtonElement | null>(null);
   const [pointWidthE, setPointWidthE] = React.useState<HTMLButtonElement | null>(null);
   const [labelFormatE, setLabelFormatE] = React.useState<HTMLButtonElement | null>(null);

   function setPointColorA(event: React.MouseEvent<HTMLButtonElement>) {
      setPointColorE(event.currentTarget);
   }
   function setPointStyleA(event: React.MouseEvent<HTMLButtonElement>) {
      setPointStyleE(event.currentTarget);
   }
   function setPointWidthA(event: React.MouseEvent<HTMLButtonElement>) {
      setPointWidthE(event.currentTarget);
   }
   function setLabelFormatA(event: React.MouseEvent<HTMLButtonElement>) {
      setLabelFormatE(event.currentTarget);
   }

   function pointColorClose() {
      setPointColorE(null);
   }
   function pointStyleClose() {
      setPointStyleE(null);
   }
   function pointWidthClose() {
      setPointWidthE(null);
   }
   function labelFormatClose() {
      setLabelFormatE(null);
   }

   //Label format dropdown click event.
   function labelFormatClick(button: string, inFormat: string) {
      labelFormatText = inFormat;
      setLabelFormatE(null);
   }

   //Simple marker symbols.
   function pointStyleClick(button: string, pointStyle: string) {
      pointSym.markerType = "Simple";

      sketchVM.pointSymbol = pointSym.simpleMarkerSym.clone();
      sketchVM.pointSymbol.style = pointStyle as markerStyleType;

      if (pointStyle === "cross" || pointStyle === "x") {
         sketchVM.pointSymbol.outline.color.setColor(pointSym.color.toLowerCase());
      } else {
         sketchVM.pointSymbol.outline.color.setColor("black");
      }

      pointSym.simpleMarkerSym = sketchVM.pointSymbol; //Save current simple symbol.
      pointSym.style = pointStyle;

      setPointStyleE(null);
   }

   //Picture marker symbols.
   function pictureMarkerClick(imageName: string, imageSrc: string) {
      pointSym.markerType = "Picture";

      sketchVM.pointSymbol = pointSym.pictureMarkerSym.clone();

      let cimPictureMarker = sketchVM.pointSymbol.data.symbol.symbolLayers[0] as __esri.CIMPictureMarker;
      cimPictureMarker.url = imageSrc;

      pointSym.pictureMarkerSym = sketchVM.pointSymbol; //Save current picture symbol.
      pointSym.style = imageName;

      setPointStyleE(null);
   }

   //Line symbology menus.
   const [lineColorE, setLineColorE] = React.useState<HTMLButtonElement | null>(null);
   const [lineStyleE, setLineStyleE] = React.useState<HTMLButtonElement | null>(null);
   const [lineWidthE, setLineWidthE] = React.useState<HTMLButtonElement | null>(null);

   function setLineColorA(event: React.MouseEvent<HTMLButtonElement>) {
      setLineColorE(event.currentTarget);
   }
   function setLineStyleA(event: React.MouseEvent<HTMLButtonElement>) {
      setLineStyleE(event.currentTarget);
   }
   function setLineWidthA(event: React.MouseEvent<HTMLButtonElement>) {
      setLineWidthE(event.currentTarget);
   }

   function lineColorClose() {
      setLineColorE(null);
   }
   function lineStyleClose() {
      setLineStyleE(null);
   }
   function lineWidthClose() {
      setLineWidthE(null);
   }

   function lineStyleClick(button: string, lineStyle: string) {
      sketchVM.polylineSymbol = sketchVM.polylineSymbol.clone() as __esri.SimpleLineSymbol;
      sketchVM.polylineSymbol.style = lineStyle as lineStyleType;
      lineSym.style = lineStyle;
      setLineStyleE(null);
   }

   //Polygon symbology menus.
   const [polyColorE, setPolyColorE] = React.useState<HTMLButtonElement | null>(null);
   const [polyOpacityE, setPolyOpacityE] = React.useState<HTMLButtonElement | null>(null);
   const [polyWidthE, setPolyWidthE] = React.useState<HTMLButtonElement | null>(null);

   function setPolyColorA(event: React.MouseEvent<HTMLButtonElement>) {
      setPolyColorE(event.currentTarget);
   }
   function setPolyOpacityA(event: React.MouseEvent<HTMLButtonElement>) {
      setPolyOpacityE(event.currentTarget);
   }
   function setPolyWidthA(event: React.MouseEvent<HTMLButtonElement>) {
      setPolyWidthE(event.currentTarget);
   }

   function polyColorClose() {
      setPolyColorE(null);
   }
   function polyOpacityClose() {
      setPolyOpacityE(null);
   }
   function polyWidthClose() {
      setPolyWidthE(null);
   }

   //Text symbology menus.
   const [textColorE, setTextColorE] = React.useState<HTMLButtonElement | null>(null);
   const [textFontE, setTextFontE] = React.useState<HTMLButtonElement | null>(null);
   const [textSizeE, setTextSizeE] = React.useState<HTMLButtonElement | null>(null);

   function setTextColorA(event: React.MouseEvent<HTMLButtonElement>) {
      setTextColorE(event.currentTarget);
   }
   function setTextFontA(event: React.MouseEvent<HTMLButtonElement>) {
      setTextFontE(event.currentTarget);
   }
   function setTextSizeA(event: React.MouseEvent<HTMLButtonElement>) {
      setTextSizeE(event.currentTarget);
   }

   function textColorClose() {
      setTextColorE(null);
   }
   function textFontClose() {
      setTextFontE(null);
   }
   function textSizeClose() {
      setTextSizeE(null);
   }

   //Text font click event.
   function textFontClick(button: string, font: string) {
      textSym.font = font;
      setTextFontE(null);
   }

   //Color click event.
   function colorClick(button: string, color: string) {

      if (button === "PointColor") {
         if (sketchVM.pointSymbol.type === "simple-marker") {
            sketchVM.pointSymbol = pointSym.simpleMarkerSym.clone();
            sketchVM.pointSymbol.color.setColor(color.toLowerCase());

            if (pointSym.style === "cross" || pointSym.style === "x") {
               sketchVM.pointSymbol.outline.color.setColor(color.toLowerCase());
            } else {
               sketchVM.pointSymbol.outline.color.setColor("black");
            }

            pointSym.simpleMarkerSym = sketchVM.pointSymbol; //Save current marker symbol.
            pointSym.color = color;
         }

         setPointColorE(null);

      } else if (button === "LineColor") {
         sketchVM.polylineSymbol = sketchVM.polylineSymbol.clone() as __esri.SimpleLineSymbol;
         sketchVM.polylineSymbol.color.setColor(color.toLowerCase());

         lineSym.color = color;
         setLineColorE(null);

      } else if (button === "PolyColor") {
         sketchVM.polygonSymbol = sketchVM.polygonSymbol.clone() as __esri.SimpleFillSymbol;
         sketchVM.polygonSymbol.color = Color.fromString(color.toLowerCase());
         sketchVM.polygonSymbol.color.a = Util.parseFlt(polySym.opacity);

         sketchVM.polygonSymbol.outline.color = Color.fromString(color.toLowerCase());
         polySym.color = color;
         setPolyColorE(null);

         resetSketchingLineSymbol();

      } else if (button === "TextColor") {
         textSym.color = color;
         setTextColorE(null);
      }
   }

   //Width and size click event.
   function widthClick(button: string, width: string) {
      if (button === "PointWidth") {

         pointSym.simpleMarkerSym.size = Util.parseFlt(width);

         let cimPictureMarker = pointSym.pictureMarkerSym.data.symbol.symbolLayers[0] as __esri.CIMPictureMarker;
         cimPictureMarker.size = Util.parseFlt(width);

         if (pointSym.markerType === "Simple") {
            sketchVM.pointSymbol = pointSym.simpleMarkerSym.clone();
            pointSym.simpleMarkerSym = sketchVM.pointSymbol; //Save current marker symbol.

         } else { //Picture marker symbol.
            sketchVM.pointSymbol = pointSym.pictureMarkerSym.clone();
            pointSym.pictureMarkerSym = sketchVM.pointSymbol; //Save current marker symbol.
         }

         pointSym.size = width;
         setPointWidthE(null);

      } else if (button === "LineWidth") {
         sketchVM.polylineSymbol = sketchVM.polylineSymbol.clone() as __esri.SimpleLineSymbol;
         sketchVM.polylineSymbol.set({ width: Util.parseFlt(width) });
         lineSym.width = width;
         setLineWidthE(null);

      } else if (button === "PolyWidth") {
         sketchVM.polygonSymbol = sketchVM.polygonSymbol.clone() as __esri.SimpleFillSymbol;
         sketchVM.polygonSymbol.outline.width = Util.parseFlt(width);
         polySym.width = width;
         setPolyWidthE(null);

         resetSketchingLineSymbol();

      } else if (button === "TextSize") {
         textSym.size = width;
         setTextSizeE(null);
      }
   }

   //Poly click events.
   function opacityClick(opacity: string) {
      sketchVM.polygonSymbol = sketchVM.polygonSymbol.clone() as __esri.SimpleFillSymbol;
      sketchVM.polygonSymbol.color.a = Util.parseFlt(opacity);
      polySym.opacity = opacity;
      setPolyOpacityE(null);
   }

   //Set the line symbol for sketch in progress.
   function resetSketchingLineSymbol() {
      if (polygonTools.includes(sketchW.activeTool)) {
         sketchVM.polylineSymbol.color.setColor(polySym.color.toLowerCase());
         sketchVM.polylineSymbol.set({ lineStyleE: "solid" });
         sketchVM.polylineSymbol.set({ lineWidthE: Util.parseFlt(polySym.width) });

      } else if (sketchW.activeTool === "point" || sketchW.activeTool === "polyline") {
         sketchVM.polylineSymbol.color.setColor(lineSym.color.toLowerCase());
         sketchVM.polylineSymbol.set({ lineStyleE: lineSym.style });
         sketchVM.polylineSymbol.set({ lineWidthE: Util.parseFlt(lineSym.width) });
      }
   }

   //Text value changed event.
   function textChanged(event: React.ChangeEvent<HTMLInputElement>) {
      textSym.text = event.target.value;
      refresh();
   }

   //Build color menu items.
   function getColorMenuItems(button: string): JSX.Element[] {
      const jsx: JSX.Element[] = [];
      const colors: string[] = ["Black", "Silver", "Gray", "White", "Maroon", "Red", "Orange", "Purple", "Fuchsia", "Green", "Lime", "Olive", "Gold", "Yellow", "Navy", "Blue", "Teal", "Aqua"];

      let iconName: string;
      if (button === "PointColor") {
         iconName = "place";

      } else if (button === "LineColor") {
         iconName = "timeline";

      } else if (button === "TextColor") {
         iconName = "font_download";

      } else {
         iconName = "color_lens";
      }

      for (const color of colors) {
         let colorName: string = color;
         if (color === "White") {
            colorName = "Gainsboro";
         }

         jsx.push(
            <MenuItem key={Util.uid()} onClick={() => colorClick(button, color)} dense>
               <ListItemIcon><Icon style={{ color: colorName }}>{iconName}</Icon></ListItemIcon>
               <ListItemText primary={color} />
            </MenuItem>,
         );
      }
      return jsx;
   }

   //Build point style menu items.
   function getPointStyleMenuItems(button: string): JSX.Element[] {
      const jsx: JSX.Element[] = [];
      let iconName: string;
      const pointStyles: string[] = ["circle", "cross", "diamond", "square", "triangle", "x",
         "Arrow", "Camera", "Car", "City_1", "City_2", "Flag_1", "Flag_2", "Flag_3", "Flag_4", "House", "Park",
         "Pin_1", "Pin_2", "Pin_3", "Pin_4", "Pin_5", "Pin_6", "Star", "Sun"];

      for (const pointStyle of pointStyles) {

         if (pointStyle === "circle") {
            iconName = "radio_button_unchecked";

         } else if (pointStyle === "cross") {
            iconName = "add";

         } else if (pointStyle === "diamond") {
            iconName = "layers";

         } else if (pointStyle === "square") {
            iconName = "check_box_outline_blank";

         } else if (pointStyle === "triangle") {
            iconName = "change_history";

         } else if (pointStyle === "x") {
            iconName = "clear";

         } else { //Picture marker symbols.
            iconName = pointStyle;
         }

         let pointStyleName: string = pointStyle.replace("_", " ").ToProperCase();
         if (pointStyleName === "X") {
            pointStyleName = "X Symbol";
         }

         //Picture marker symbols.
         if (iconName === pointStyle) {
            const imageSrc: string = "Images/" + iconName + ".png";

            jsx.push(
               <MenuItem key={Util.uid()} onClick={() => pictureMarkerClick(pointStyle, imageSrc)} dense>
                  <img src={imageSrc} alt={iconName} />
                  <span style={{ marginLeft: 20 }}></span>
                  <ListItemText primary={pointStyleName} />
               </MenuItem>,
            );

         } else { //Simple marker symbols.
            jsx.push(
               <MenuItem key={Util.uid()} onClick={() => pointStyleClick(button, pointStyle)} dense>
                  <ListItemIcon><Icon>{iconName}</Icon></ListItemIcon>
                  <ListItemText primary={pointStyleName} />
               </MenuItem>,
            );
         }
      }
      return jsx;
   }

   //Build line style menu items.
   function getLineStyleMenuItems(button: string): JSX.Element[] {
      const jsx: JSX.Element[] = [];
      const lineStyles: string[] = ["solid", "short-dash", "short-dash-dot", "short-dash-dot-dot", "short-dot"];

      let pattern: string;

      for (const lineStyle of lineStyles) {
         if (lineStyle === "solid") {
            pattern = "_____   ";

         } else if (lineStyle === "short-dash") {
            pattern = "_  _  _   ";

         } else if (lineStyle === "short-dash-dot") {
            pattern = "_  .  _  .  ";

         } else if (lineStyle === "short-dash-dot-dot") {
            pattern = "_  .  .     ";

         } else if (lineStyle === "short-dot") {
            pattern = ".  .  .  .   ";
         }

         const styleText: string = lineStyle.replace("short-", "").replace("-", " ").replace("-", " ");

         jsx.push(
            <MenuItem key={Util.uid()} onClick={() => lineStyleClick(button, lineStyle)} dense>
               <ListItemText primary={
                  <React.Fragment>
                     <Typography style={{ verticalAlign: "top", fontWeight: "600", lineHeight: "100%", letterSpacing: "-1px", whiteSpace: "pre" }}
                        component="span"
                        variant="body2"
                        color="primary"
                     >
                        {pattern}
                     </Typography>
                     {styleText}
                  </React.Fragment>
               } />
            </MenuItem>,
         );
      }
      return jsx;
   }

   //Build font menu items.
   function getFontMenuItems(button: string): JSX.Element[] {
      const jsx: JSX.Element[] = [];
      const fonts: string[] = ["Sans-serif", "Serif", "Monospace", "Cursive", "Fantasy"];

      for (const font of fonts) {
         jsx.push(
            <MenuItem key={Util.uid()} onClick={() => textFontClick(button, font)} dense>
               <ListItemText primary={font} />
            </MenuItem>,
         );
      }
      return jsx;
   }

   //Build point size menu items.
   function getSizeMenuItems(button: string): JSX.Element[] {
      const jsx: JSX.Element[] = [];
      const sizes: string[] = ["6", "8", "10", "12", "14", "18", "24", "36"];

      for (const size of sizes) {
         const primText: string = "Size: " + size + " pt";
         jsx.push(
            <MenuItem key={Util.uid()} onClick={() => widthClick(button, size)} dense>
               <ListItemText primary={primText} />
            </MenuItem>,
         );
      }
      return jsx;
   }

   //Build line and outline width menu items.
   function getWidthMenuItems(button: string): JSX.Element[] {
      const jsx: JSX.Element[] = [];
      const widths: string[] = ["0.75", "1", "1.5", "2", "2.5", "3", "4", "5", "6", "8", "10"];

      for (const width of widths) {
         const primText: string = "width: " + width + " pt";
         jsx.push(
            <MenuItem key={Util.uid()} onClick={() => widthClick(button, width)} dense>
               <ListItemText primary={primText} />
            </MenuItem>,
         );
      }
      return jsx;
   }

   //Build opacity menu items.
   function getOpacityMenuItems(): JSX.Element[] {
      const jsx: JSX.Element[] = [];
      const opacities: string[] = ["0", "0.1", "0.2", "0.3", "0.4", "0.5", "0.6", "0.7", "0.8", "0.9", "1"];

      for (const opacity of opacities) {
         const primText: string = "opacity: " + (Util.parseFlt(opacity) * 100) + "%";

         jsx.push(
            <MenuItem key={Util.uid()} onClick={() => opacityClick(opacity)} dense>
               <ListItemText primary={primText} />
            </MenuItem>,
         );
      }
      return jsx;
   }

   //Build coordinate format items.
   function getLabelFormatItems(button: string): JSX.Element[] {
      const jsx: JSX.Element[] = [];
      const formats: string[] = ["Lat/Long DM", "Lat/Long DMS", "Lat/Long DD", "XY Map Units"];

      for (const f of formats) {
         jsx.push(
            <MenuItem key={Util.uid()} onClick={() => labelFormatClick(button, f)} dense>
               <ListItemText primary={f} />
            </MenuItem>,
         );
      }
      return jsx;
   }

   //Add text to map.
   function addText(pnt: __esri.Point) {
      if (textSym.text !== "") {
         GraLay.temp.removeAll();
         const gra = textGra.clone();
         gra.geometry = pnt;
         GraLay.sketch.add(gra);
         refresh();
      }
   }

   //Add coordinate label to map.
   function addCoordLabel(pnt: __esri.Point) {
      let sym = textSymbol.clone();

      sym.color = Color.fromString(textSym.color.toLowerCase());
      sym.font.family = textSym.font.toLowerCase();
      sym.font.size = Util.parseInteger(textSym.size);

      sym.haloColor = Color.fromString("black");
      if (textSym.color === "Black" || textSym.color === "Maroon" || textSym.color === "Purple" || textSym.color === "Green" || textSym.color === "Olive" || textSym.color === "Navy" || textSym.color === "Blue" || textSym.color === "Teal") {
         sym.haloColor = Color.fromString("white");
      }

      sym.verticalAlignment = "bottom";
      sym.yoffset = sym.font.size / 1.5;

      sym.horizontalAlignment = "left";
      sym.xoffset = sym.font.size / 1.5;

      //Build coordinate label text.
      let latY: string = Math.round(pnt.x).toString(); //Flipped so x will be first.
      let lonX: string = Math.round(pnt.y).toString();

      if (labelFormatText.StartsWith("Lat")) {
         //Project to lat/long.
         const outSpatialReference = new SpatialReference({ wkid: 4326 });
         const geom = projection.project(pnt, outSpatialReference) as __esri.Point;

         latY = Util.formatTextValue(geom.y, "Round5", true);
         lonX = Util.formatTextValue(geom.x, "Round5", true);

         if (labelFormatText === "Lat/Long DM") {
            latY = Util_App.ddToDM(geom.y, false);
            lonX = Util_App.ddToDM(geom.x, false);

         } else if (labelFormatText === "Lat/Long DMS") {
            latY = Util_App.ddToDMS(geom.y, false, true);
            lonX = Util_App.ddToDMS(geom.x, false, true);
         }
      }

      sym.text = latY + ", " + lonX;

      //Add graphic text.
      const gra = new Graphic({ geometry: pnt, symbol: sym });
      //Util.errMsg("gra", gra);
      GraLay.sketch.add(gra);
   }

   //Save sketch.
   function saveSketch() {
      const graphics = GraLay.sketch.graphics.toArray(); // Convert graphics to an array
      localStorage.SetItem("SavedSketch", graphics);
      refresh();
   }

   //Restore last saved sketch.
   function restoreSketch() {
      const graphics = localStorage.GetItem("SavedSketch") as __esri.Graphic[];
      GraLay.sketch.removeAll();

      for (const gra of graphics) {
         const sym = gra.symbol as __esri.Symbol | __esri.CIMSymbolReference;
         const symType = sym.type as string;

         let geom: __esri.Geometry;
         let newSym: __esri.Symbol;

         //Util.errMsg("In Gra", gra);

         //Point. ==================================================================
         if (symType === "esriSMS") {
            const point = gra.geometry as __esri.Point;
            geom = new Point({ x: point.x, y: point.y, spatialReference: MainMap.activeView.spatialReference });

            const simpleMarkerSymbol = sym as __esri.SimpleMarkerSymbol;
            const pointStyle = simpleMarkerSymbol.style.replace("esriSMS", "").toLowerCase() as markerStyleType;

            newSym = new SimpleMarkerSymbol({
               color: simpleMarkerSymbol.color, style: pointStyle, size: simpleMarkerSymbol.size,
               outline: { color: simpleMarkerSymbol.outline.color }
            });

         //Picture point. ==================================================================
         } else if (symType === "CIMSymbolReference") {
            const point = gra.geometry as __esri.Point;
            geom = new Point({ x: point.x, y: point.y, spatialReference: MainMap.activeView.spatialReference });

            const cimSymbolReference = sym as __esri.CIMSymbolReference;
            const cimPictureMarker = cimSymbolReference.symbol.symbolLayers[0] as __esri.CIMPictureMarker;

            newSym = new CIMSymbol({
               data: {
                  type: "CIMSymbolReference",
                  symbol: {
                     type: "CIMPointSymbol",
                     symbolLayers: [{
                        "type": "CIMPictureMarker",
                        "enable": true,
                        size: cimPictureMarker.size,
                        "url": cimPictureMarker.url
                     }]
                  }
               }
            });

         //Text. ==================================================================
         } else if (symType === "esriTS") {
            const point = gra.geometry as __esri.Point;
            geom = new Point({ x: point.x, y: point.y, spatialReference: MainMap.activeView.spatialReference });

            const textSymbol = sym as __esri.TextSymbol;

            newSym = new TextSymbol( {
               color: textSymbol.color, haloColor: textSymbol.haloColor, haloSize: textSymbol.haloSize, text: textSymbol.text,
               verticalAlignment: textSymbol.verticalAlignment, yoffset: textSymbol.yoffset,
               horizontalAlignment: textSymbol.horizontalAlignment, xoffset: textSymbol.horizontalAlignment,
               font: { family: textSymbol.font.family, size: textSymbol.font.size },
            });

         //Line. ==================================================================
         } else if (symType === "esriSLS") {
            const polyline = gra.geometry as __esri.Polyline;
            geom = new Polyline({ paths: polyline.paths, hasZ: false, hasM: false, spatialReference: MainMap.activeView.spatialReference });

            const simpleLineSymbol = sym as __esri.SimpleLineSymbol;

            const lineStyle = simpleLineSymbol.style.replace("esriSLS", "")
               .replace("ShortDash", "Short-Dash").replace("DashDot", "Dash-Dot")
               .replace("DotDot", "Dot-Dot").replace("ShortDot", "Short-Dot")
               .toLowerCase() as lineStyleType;

            newSym = new SimpleLineSymbol({ color: simpleLineSymbol.color, style: lineStyle, width: simpleLineSymbol.width });

         //Polygon. ==================================================================
         } else if (symType === "esriSFS") {
            const polygon = gra.geometry as __esri.Polygon;
            geom = new Polygon({ rings: polygon.rings, hasZ: false, hasM: false, spatialReference: MainMap.activeView.spatialReference });

            const simpleFillSymbol = sym as __esri.SimpleFillSymbol;
            const newColor = new Color([simpleFillSymbol.color[0], simpleFillSymbol.color[1], simpleFillSymbol.color[2], simpleFillSymbol.color[3] / 255]);

            newSym = new SimpleFillSymbol({
               color: newColor,
               outline: { color: simpleFillSymbol.outline.color, width: simpleFillSymbol.outline.width }
            });
         } //end if.

         //Restore the graphic.
         const newGra = new Graphic({ geometry: geom, symbol: newSym });
         GraLay.sketch.add(newGra);

      } //Next graphic.
      refresh();
   }

   //Add label coordinate checkbox was clicked.
   function addLabelChanged(event: React.ChangeEvent<HTMLInputElement>) {
      addLabelCoord = event.target.checked;
      refresh();
   }

   //Refresh section.
   let clearAllDisabled: boolean = false;
   if (GraLay.sketch.graphics.length === 0) {
      clearAllDisabled = true;
   }

   let restoreDisabled: boolean = true;
   if (localStorage.Exists("SavedSketch")) {
      restoreDisabled = false;
   }

   pointSym.visible = false;
   lineSym.visible = false;
   polySym.visible = false;
   textSym.visible = false;

   if (drawingToolIsText) {
      textSym.visible = true;

   } else if (sketchW && sketchW.activeTool) {
      if (sketchW.activeTool === "point") {
         pointSym.visible = true;

      } else if (sketchW.activeTool === "polyline") {
         lineSym.visible = true;

      } else if (polygonTools.includes(sketchW.activeTool)) {
         polySym.visible = true;
      }
   }

   const pointStyleText: string = pointSym.style.ToProperCase().replace("_"," ");
   const lineStyleText: string = lineSym.style.replace("short-", "").replace("-", " ").replace("-", " ");
   const textFont: string = textSym.font;

   const pointSizeText: string = pointSym.size + " pt";
   const lineWidthText: string = lineSym.width + " pt";
   const polyWidthText: string = polySym.width + " pt";
   const textSize: string = textSym.size + " pt";

   const opacityText: string = (Util.parseFlt(polySym.opacity) * 100) + "%";

   const pointStyleIcon = <Icon color="action">category</Icon>;
   const pointWidthIcon = <Icon color="action">edit_location</Icon>;

   const lineStyleIcon = <Icon color="action">line_style</Icon>;
   const lineWidthIcon = <Icon color="action">line_weight</Icon>;

   const textFontIcon = <Icon color="action" style={{ fontSize: 24 }}>text_format</Icon>;
   const textSizeIcon = <Icon color="action">text_fields</Icon>;

   const opacityIcon = <Icon color="action">opacity</Icon>;

   const pointIcon = <Icon style={{ color: pointSym.color }}>place</Icon>;
   const lineIcon = <Icon style={{ color: lineSym.color }}>timeline</Icon>;
   const polyIcon = <Icon style={{ color: polySym.color }}>color_lens</Icon>;
   const textIcon = <Icon style={{ color: textSym.color }}>font_download</Icon>;
   const dropDownIcon = <Icon style={{ color: textSym.color }}>arrow_drop_down</Icon>;

   const styles: Default.iStyles = {
      toolStyle: { width: Settings.leftPanelWidth, maxWidth: Settings.leftPanelWidth, padding: 5, paddingTop: 0, paddingBottom: 3, overflow: 'auto' },
      textBox: { width: 315, marginLeft: 9, marginTop: 6, padding: 2, fontFamily: 'Roboto, Helvetica, Arial', fontSize: 'medium', border: '1px solid #aaa', borderRadius: 3 },
      help: { padding: 7, marginTop: 3, marginBottom: 2, backgroundColor: yellow[200] },
   };

   return (
      <Paper>
         <WidgetHeading.Render title={toolName} onClose={close} />

         <div style={styles.toolStyle}>
            <div id={toolDiv} style={{ backgroundColor: Settings.paperColor }}></div>

            <Typography variant="body2" style={styles.help}>
               {helpMsg}
            </Typography>

            {/* ================================================================================== */}
            {pointSym.visible && <div>
               <ButtonGroup color="info" variant="text" fullWidth aria-label="Symbology">
                  <Button startIcon={pointIcon} onClick={setPointColorA} aria-controls="point-color-menu" title="Choose drawing color" aria-label="Drawing color">
                     {pointSym.color}
                  </Button>

                  <Button startIcon={pointStyleIcon} onClick={setPointStyleA} aria-controls="point-style-menu" title="Choose point style" aria-label="Point style">
                     {pointStyleText}
                  </Button>

                  <Button startIcon={pointWidthIcon} onClick={setPointWidthA} aria-controls="point-width-menu" title="Choose point size" aria-label="Point size">
                     {pointSizeText}
                  </Button>
               </ButtonGroup>

               <div className="app-flex-row-start" style={{ marginTop: 9, marginLeft: 28 }}>
                  <FormControlLabel title="Label coordinate"
                     control={<Checkbox checked={addLabelCoord} color="primary" onChange={addLabelChanged} />}
                     label="Label coordinate"
                  />

                  <Button onClick={setLabelFormatA} endIcon={dropDownIcon} color="info" aria-controls="label-format-menu" title="Choose coordinate format" aria-label="Coordinate format">
                     {labelFormatText}
                  </Button>
               </div>
            </div>}

            <Menu id="point-color-menu" anchorEl={pointColorE} open={Boolean(pointColorE)} onClose={pointColorClose}>
               {getColorMenuItems("PointColor")}
            </Menu>

            <Menu id="point-style-menu" anchorEl={pointStyleE} open={Boolean(pointStyleE)} onClose={pointStyleClose}>
               {getPointStyleMenuItems("PointStyle")}
            </Menu>

            <Menu id="point-width-menu" anchorEl={pointWidthE} open={Boolean(pointWidthE)} onClose={pointWidthClose}>
               {getSizeMenuItems("PointWidth")}
            </Menu>

            <Menu id="label-format-menu" anchorEl={labelFormatE} open={Boolean(labelFormatE)} onClose={labelFormatClose}>
               {getLabelFormatItems("LabelFormat")}
            </Menu>

            {/* ================================================================================== */}
            {lineSym.visible &&
               <ButtonGroup color="info" variant="text" fullWidth aria-label="Symbology">
                  <Button startIcon={lineIcon} endIcon={dropDownIcon} onClick={setLineColorA} aria-controls="line-color-menu" title="Choose drawing color" aria-label="Drawing color">
                     {lineSym.color}
                  </Button>

                  <Button startIcon={lineStyleIcon} endIcon={dropDownIcon} onClick={setLineStyleA} aria-controls="line-style-menu" title="Choose line style" aria-label="Line style">
                     {lineStyleText}
                  </Button>

                  <Button startIcon={lineWidthIcon} endIcon={dropDownIcon} onClick={setLineWidthA} aria-controls="line-width-menu" title="Choose line width" aria-label="Line width">
                     {lineWidthText}
                  </Button>
               </ButtonGroup>
            }

            <Menu id="line-color-menu" anchorEl={lineColorE} open={Boolean(lineColorE)} onClose={lineColorClose}>
               {getColorMenuItems("LineColor")}
            </Menu>

            <Menu id="line-style-menu" anchorEl={lineStyleE} open={Boolean(lineStyleE)} onClose={lineStyleClose}>
               {getLineStyleMenuItems("LineStyle")}
            </Menu>

            <Menu id="line-width-menu" anchorEl={lineWidthE} open={Boolean(lineWidthE)} onClose={lineWidthClose}>
               {getWidthMenuItems("LineWidth")}
            </Menu>

            {/* ================================================================================== */}
            {polySym.visible &&
               <ButtonGroup color="info" variant="text" fullWidth aria-label="Symbology">
                  <Button startIcon={polyIcon} endIcon={dropDownIcon} onClick={setPolyColorA} aria-controls="poly-color-menu" title="Choose drawing color" aria-label="Drawing color">
                     {polySym.color}
                  </Button>

                  <Button startIcon={opacityIcon} endIcon={dropDownIcon} onClick={setPolyOpacityA} aria-controls="poly-opacity-menu" title="Choose color opacity" aria-label="Color opacity">
                     {opacityText}
                  </Button>

                  <Button startIcon={lineWidthIcon} endIcon={dropDownIcon} onClick={setPolyWidthA} aria-controls="poly-width-menu" title="Choose outline width" aria-label="Polygon width">
                     {polyWidthText}
                  </Button>
               </ButtonGroup>
            }

            <Menu id="poly-color-menu" anchorEl={polyColorE} open={Boolean(polyColorE)} onClose={polyColorClose}>
               {getColorMenuItems("PolyColor")}
            </Menu>

            <Menu id="poly-opacity-menu" anchorEl={polyOpacityE} open={Boolean(polyOpacityE)} onClose={polyOpacityClose}>
               {getOpacityMenuItems()}
            </Menu>

            <Menu id="poly-width-menu" anchorEl={polyWidthE} open={Boolean(polyWidthE)} onClose={polyWidthClose}>
               {getWidthMenuItems("PolyWidth")}
            </Menu>

            {/* ================================================================================== */}
            {textSym.visible &&
               <div>
                  <ButtonGroup color="info" variant="text" fullWidth aria-label="Symbology">
                     <Button startIcon={textIcon} onClick={setTextColorA} aria-controls="text-color-menu" title="Choose text color" aria-label="Text color">
                        {textSym.color}
                     </Button>

                     <Button startIcon={textFontIcon} onClick={setTextFontA} aria-controls="text-font-menu" title="Choose font style" aria-label="Text font">
                        {textFont}
                     </Button>

                     <Button startIcon={textSizeIcon} onClick={setTextSizeA} aria-controls="text-size-menu" title="Choose text size" aria-label="Font size">
                        {textSize}
                     </Button>
                  </ButtonGroup>

                  <input type="text" style={styles.textBox} value={textSym.text} onChange={textChanged} autoFocus />
               </div>
            }

            <Menu id="text-color-menu" anchorEl={textColorE} open={Boolean(textColorE)} onClose={textColorClose}>
               {getColorMenuItems("TextColor")}
            </Menu>

            <Menu id="text-font-menu" anchorEl={textFontE} open={Boolean(textFontE)} onClose={textFontClose}>
               {getFontMenuItems("TextFont")}
            </Menu>

            <Menu id="text-size-menu" anchorEl={textSizeE} open={Boolean(textSizeE)} onClose={textSizeClose}>
               {getSizeMenuItems("TextSize")}
            </Menu>

            {/* ================================================================================== */}
            <div className="app-flex-row-around" style={{ marginTop: 9 }}>
               <Button variant="text" size="large" color="info" onClick={activateTextTool} title="Add text to map" aria-label="Add text">
                  Add Text
               </Button>

               <IconButton color="default" disabled={clearAllDisabled} onClick={saveSketch} title="Save sketch" aria-label="save">
                  <Icon color="action">save</Icon>
               </IconButton>

               <IconButton color="default" disabled={restoreDisabled} onClick={restoreSketch} title="Restore last saved sketch" aria-label="restore">
                  <Icon color="action">restore_page</Icon>
               </IconButton>

               <Button variant="text" color="info" onClick={deleteAll} disabled={clearAllDisabled} title="Delete all graphics" aria-label="Delete all">
                  <Icon color="action">delete</Icon>
                  <span style={{ marginLeft: 6 }}></span>
                  Delete All
               </Button>
            </div>

         </div>

      </Paper>
   ); //end return.

} //end function.
